import { Button, Tooltip } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { AiOutlineSwap } from 'react-icons/ai';

import { useProject } from '@contexts/Project.context';
import { useProjectMode } from '@contexts/ProjectMode.context';
import { buildProjectUrl, stripModeSearchParams } from '@utils/projectPath';
import { MODEL_VIEW_MODE_ENABLED, PROJECT_VIEW_MODE } from '@utils/constants';

const OTHER_MODE = Object.freeze({
	[PROJECT_VIEW_MODE.MODEL]: PROJECT_VIEW_MODE.COLLECTION,
	[PROJECT_VIEW_MODE.COLLECTION]: PROJECT_VIEW_MODE.MODEL,
});

// Labels describe the destination, not the current mode.
const TARGET_LABEL = Object.freeze({
	[PROJECT_VIEW_MODE.MODEL]: 'Model-based view',
	[PROJECT_VIEW_MODE.COLLECTION]: 'Collection view',
});

/**
 * Button in the Layers sidebar header that moves the current project to the
 * other view mode.
 *
 * The route is the single source of truth for the mode, so this only
 * navigates; ProjectModeProvider remounts the page under the new mode.
 */
const ProjectViewModeSwitch = () => {
	const navigate = useNavigate();
	const { project_uuid: projectUuid } = useParams();
	const [searchParams] = useSearchParams();
	const projectViewMode = useProjectMode();
	const { annotationMode } = useProject();

	if (!MODEL_VIEW_MODE_ENABLED || annotationMode || !projectUuid) return null;

	const targetMode = OTHER_MODE[projectViewMode];
	const label = TARGET_LABEL[targetMode];

	// Shared params (e.g. showTrainingData) travel with the user; the other
	// mode's picker selection doesn't.
	const switchMode = () =>
		navigate(
			`${buildProjectUrl(projectUuid, targetMode)}${stripModeSearchParams(searchParams)}`
		);

	return (
		<Tooltip title={`Switch to the ${label.toLowerCase()}`}>
			<Button
				data-tour="mode-switch"
				size="small"
				color="primary"
				variant="outlined"
				onClick={switchMode}
				icon={<AiOutlineSwap aria-hidden="true" />}>
				{label}
			</Button>
		</Tooltip>
	);
};

export default ProjectViewModeSwitch;
